import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
  Divider,
  Alert,
  Slider,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  Collapse,
  alpha
} from '@mui/material';
import {
  Settings as SettingsIcon,
  CloudDownload as CloudDownloadIcon,
  Folder as FolderIcon,
  Speed as SpeedIcon,
  Subtitles as SubtitlesIcon,
  Language as LanguageIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  DarkMode as DarkModeIcon,
  Storage as StorageIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useDownload } from '../contexts/DownloadContext';
import toast from 'react-hot-toast';

const SettingsPage = () => {
  const { downloadHistory, clearHistory } = useDownload();
  
  // Download Settings
  const [downloadSettings, setDownloadSettings] = useState({
    defaultFormat: 'video',
    defaultQuality: '1080p',
    concurrentDownloads: 3,
    maxFileSize: 1024, // MB
    downloadPath: '/downloads',
    createSubfolders: true,
    includeMetadata: true,
    includeSubtitles: false,
    subtitleLanguage: 'en'
  });

  // Performance Settings
  const [performanceSettings, setPerformanceSettings] = useState({
    maxDownloadSpeed: 0, // 0 = unlimited
    enableThrottling: false,
    cacheSize: 512, // MB
    clearCacheOnExit: false,
    enableHardwareAcceleration: true
  });

  // Appearance Settings
  const [appearanceSettings, setAppearanceSettings] = useState({
    theme: 'dark',
    compactMode: false,
    showThumbnails: true,
    animationsEnabled: true,
    language: 'en'
  });

  // Privacy Settings
  const [privacySettings, setPrivacySettings] = useState({
    saveHistory: true,
    anonymousUsage: true,
    autoDeleteHistory: false,
    historyRetentionDays: 30,
    enableAnalytics: false
  });

  // Notification Settings
  const [notificationSettings, setNotificationSettings] = useState({
    enableNotifications: true,
    notifyOnComplete: true,
    notifyOnError: true,
    soundOnComplete: false
  });

  const [expandedSection, setExpandedSection] = useState('download');
  const [cacheSize, setCacheSize] = useState(0);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Simulate cache size calculation
  useEffect(() => {
    setCacheSize(Math.floor(Math.random() * 500) + 100); // Mock cache size
  }, []);

  const handleSaveSettings = () => {
    // Save to localStorage or backend
    localStorage.setItem('appSettings', JSON.stringify({
      download: downloadSettings,
      performance: performanceSettings,
      appearance: appearanceSettings,
      privacy: privacySettings,
      notifications: notificationSettings
    }));
    toast.success('Settings saved successfully');
  };

  const handleResetSettings = () => {
    if (window.confirm('Reset all settings to default?')) {
      // Reset to defaults
      setDownloadSettings({
        defaultFormat: 'video',
        defaultQuality: '1080p',
        concurrentDownloads: 3,
        maxFileSize: 1024,
        downloadPath: '/downloads',
        createSubfolders: true,
        includeMetadata: true,
        includeSubtitles: false,
        subtitleLanguage: 'en'
      });
      setPerformanceSettings({
        maxDownloadSpeed: 0,
        enableThrottling: false,
        cacheSize: 512,
        clearCacheOnExit: false,
        enableHardwareAcceleration: true
      });
      toast.success('Settings reset to default');
    }
  };

  const handleClearCache = () => {
    if (window.confirm('Clear all cached data?')) {
      setCacheSize(0);
      toast.success('Cache cleared successfully');
    }
  };

  const handleClearAllHistory = () => {
    if (window.confirm('Clear all download history?')) {
      clearHistory();
      toast.success('Download history cleared');
    }
  };

  const renderSection = (id, icon, title, content) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            cursor: 'pointer'
          }}
          onClick={() => setExpandedSection(expandedSection === id ? '' : id)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
              {icon}
            </Avatar>
            <Typography variant="h6">{title}</Typography>
          </Box>
          {expandedSection === id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
        
        <Collapse in={expandedSection === id}>
          <Box sx={{ mt: 3 }}>
            {content}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <SettingsIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure your downloader preferences
            </Typography>
          </Box>
        </Box>

        {/* Download Settings Section */}
        {renderSection('download', <CloudDownloadIcon />, 'Download Settings', (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Default Format</InputLabel>
                <Select
                  value={downloadSettings.defaultFormat}
                  onChange={(e) => setDownloadSettings({...downloadSettings, defaultFormat: e.target.value})}
                  label="Default Format"
                >
                  <MenuItem value="video">Video (MP4)</MenuItem>
                  <MenuItem value="audio">Audio (MP3)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Default Quality</InputLabel>
                <Select
                  value={downloadSettings.defaultQuality}
                  onChange={(e) => setDownloadSettings({...downloadSettings, defaultQuality: e.target.value})}
                  label="Default Quality"
                >
                  <MenuItem value="best">Best Available</MenuItem>
                  <MenuItem value="2160p">4K (2160p)</MenuItem>
                  <MenuItem value="1080p">1080p (Full HD)</MenuItem>
                  <MenuItem value="720p">720p (HD)</MenuItem>
                  <MenuItem value="480p">480p (SD)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Concurrent Downloads</InputLabel>
                <Select
                  value={downloadSettings.concurrentDownloads}
                  onChange={(e) => setDownloadSettings({...downloadSettings, concurrentDownloads: e.target.value})}
                  label="Concurrent Downloads"
                >
                  {[1, 2, 3, 5, 10].map(num => (
                    <MenuItem key={num} value={num}>{num}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Max File Size (MB)"
                value={downloadSettings.maxFileSize}
                onChange={(e) => setDownloadSettings({...downloadSettings, maxFileSize: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Download Path"
                value={downloadSettings.downloadPath}
                onChange={(e) => setDownloadSettings({...downloadSettings, downloadPath: e.target.value})}
                InputProps={{
                  startAdornment: <FolderIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={downloadSettings.createSubfolders}
                    onChange={(e) => setDownloadSettings({...downloadSettings, createSubfolders: e.target.checked})}
                  />
                }
                label="Create subfolders for playlists"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={downloadSettings.includeMetadata}
                    onChange={(e) => setDownloadSettings({...downloadSettings, includeMetadata: e.target.checked})}
                  />
                }
                label="Include metadata in files"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={downloadSettings.includeSubtitles}
                    onChange={(e) => setDownloadSettings({...downloadSettings, includeSubtitles: e.target.checked})}
                  />
                }
                label="Download subtitles when available"
              />
            </Grid>
            {downloadSettings.includeSubtitles && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Subtitle Language</InputLabel>
                  <Select
                    value={downloadSettings.subtitleLanguage}
                    onChange={(e) => setDownloadSettings({...downloadSettings, subtitleLanguage: e.target.value})}
                    label="Subtitle Language"
                  >
                    <MenuItem value="en">English</MenuItem>
                    <MenuItem value="es">Spanish</MenuItem>
                    <MenuItem value="fr">French</MenuItem>
                    <MenuItem value="de">German</MenuItem>
                    <MenuItem value="it">Italian</MenuItem>
                    <MenuItem value="pt">Portuguese</MenuItem>
                    <MenuItem value="ru">Russian</MenuItem>
                    <MenuItem value="ja">Japanese</MenuItem>
                    <MenuItem value="ko">Korean</MenuItem>
                    <MenuItem value="zh">Chinese</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        ))}

        {/* Performance Settings Section */}
        {renderSection('performance', <SpeedIcon />, 'Performance', (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography gutterBottom>Maximum Download Speed</Typography>
              <Slider
                value={performanceSettings.maxDownloadSpeed}
                onChange={(e, val) => setPerformanceSettings({...performanceSettings, maxDownloadSpeed: val})}
                valueLabelDisplay="auto"
                step={100}
                marks={[
                  { value: 0, label: 'Unlimited' },
                  { value: 500, label: '500 KB/s' },
                  { value: 1000, label: '1 MB/s' },
                  { value: 5000, label: '5 MB/s' },
                  { value: 10000, label: '10 MB/s' }
                ]}
                min={0}
                max={10000}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={performanceSettings.enableThrottling}
                    onChange={(e) => setPerformanceSettings({...performanceSettings, enableThrottling: e.target.checked})}
                  />
                }
                label="Enable bandwidth throttling"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography gutterBottom>Cache Size</Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Slider
                  value={performanceSettings.cacheSize}
                  onChange={(e, val) => setPerformanceSettings({...performanceSettings, cacheSize: val})}
                  valueLabelDisplay="auto"
                  step={64}
                  marks={[
                    { value: 128, label: '128 MB' },
                    { value: 512, label: '512 MB' },
                    { value: 1024, label: '1 GB' },
                    { value: 2048, label: '2 GB' }
                  ]}
                  min={128}
                  max={4096}
                  sx={{ flex: 1 }}
                />
                <Chip 
                  icon={<StorageIcon />} 
                  label={`Current: ${cacheSize} MB`}
                  size="small"
                  color="primary"
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={performanceSettings.clearCacheOnExit}
                      onChange={(e) => setPerformanceSettings({...performanceSettings, clearCacheOnExit: e.target.checked})}
                    />
                  }
                  label="Clear cache on exit"
                />
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={handleClearCache}
                  startIcon={<DeleteIcon />}
                >
                  Clear Cache Now
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={performanceSettings.enableHardwareAcceleration}
                    onChange={(e) => setPerformanceSettings({...performanceSettings, enableHardwareAcceleration: e.target.checked})}
                  />
                }
                label="Enable hardware acceleration"
              />
            </Grid>
          </Grid>
        ))}

        {/* Privacy Settings Section */}
        {renderSection('privacy', <SecurityIcon />, 'Privacy', (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={privacySettings.saveHistory}
                    onChange={(e) => setPrivacySettings({...privacySettings, saveHistory: e.target.checked})}
                  />
                }
                label="Save download history"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={privacySettings.anonymousUsage}
                    onChange={(e) => setPrivacySettings({...privacySettings, anonymousUsage: e.target.checked})}
                  />
                }
                label="Send anonymous usage data"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={privacySettings.autoDeleteHistory}
                    onChange={(e) => setPrivacySettings({...privacySettings, autoDeleteHistory: e.target.checked})}
                  />
                }
                label="Auto-delete history"
              />
            </Grid>
            {privacySettings.autoDeleteHistory && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Delete after (days)</InputLabel>
                  <Select
                    value={privacySettings.historyRetentionDays}
                    onChange={(e) => setPrivacySettings({...privacySettings, historyRetentionDays: e.target.value})}
                    label="Delete after (days)"
                  >
                    <MenuItem value={7}>7 days</MenuItem>
                    <MenuItem value={30}>30 days</MenuItem>
                    <MenuItem value={60}>60 days</MenuItem>
                    <MenuItem value={90}>90 days</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={privacySettings.enableAnalytics}
                    onChange={(e) => setPrivacySettings({...privacySettings, enableAnalytics: e.target.checked})}
                  />
                }
                label="Enable analytics"
              />
            </Grid>
            <Grid item xs={12}>
              <Button 
                variant="outlined" 
                color="error"
                onClick={handleClearAllHistory}
                startIcon={<DeleteIcon />}
              >
                Clear Download History
              </Button>
            </Grid>
          </Grid>
        ))}

        {/* Appearance Settings Section */}
        {renderSection('appearance', <DarkModeIcon />, 'Appearance', (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Theme</InputLabel>
                <Select
                  value={appearanceSettings.theme}
                  onChange={(e) => setAppearanceSettings({...appearanceSettings, theme: e.target.value})}
                  label="Theme"
                >
                  <MenuItem value="light">Light</MenuItem>
                  <MenuItem value="dark">Dark</MenuItem>
                  <MenuItem value="system">System Default</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Language</InputLabel>
                <Select
                  value={appearanceSettings.language}
                  onChange={(e) => setAppearanceSettings({...appearanceSettings, language: e.target.value})}
                  label="Language"
                >
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="es">Español</MenuItem>
                  <MenuItem value="fr">Français</MenuItem>
                  <MenuItem value="de">Deutsch</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={appearanceSettings.compactMode}
                    onChange={(e) => setAppearanceSettings({...appearanceSettings, compactMode: e.target.checked})}
                  />
                }
                label="Compact mode"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={appearanceSettings.showThumbnails}
                    onChange={(e) => setAppearanceSettings({...appearanceSettings, showThumbnails: e.target.checked})}
                  />
                }
                label="Show thumbnails"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={appearanceSettings.animationsEnabled}
                    onChange={(e) => setAppearanceSettings({...appearanceSettings, animationsEnabled: e.target.checked})}
                  />
                }
                label="Enable animations"
              />
            </Grid>
          </Grid>
        ))}

        {/* Notification Settings Section */}
        {renderSection('notifications', <NotificationsIcon />, 'Notifications', (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.enableNotifications}
                    onChange={(e) => setNotificationSettings({...notificationSettings, enableNotifications: e.target.checked})}
                  />
                }
                label="Enable notifications"
              />
            </Grid>
            {notificationSettings.enableNotifications && (
              <>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notificationSettings.notifyOnComplete}
                        onChange={(e) => setNotificationSettings({...notificationSettings, notifyOnComplete: e.target.checked})}
                      />
                    }
                    label="Notify when download completes"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notificationSettings.notifyOnError}
                        onChange={(e) => setNotificationSettings({...notificationSettings, notifyOnError: e.target.checked})}
                      />
                    }
                    label="Notify on download errors"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notificationSettings.soundOnComplete}
                        onChange={(e) => setNotificationSettings({...notificationSettings, soundOnComplete: e.target.checked})}
                      />
                    }
                    label="Play sound when download completes"
                  />
                </Grid>
              </>
            )}
          </Grid>
        ))}

        <Divider sx={{ my: 3 }} />

        {/* Save Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={handleResetSettings}>
            Reset to Default
          </Button>
          <Button variant="contained" onClick={handleSaveSettings}>
            Save Settings
          </Button>
        </Box>

        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>Supported Platforms:</strong> YouTube, Facebook, Instagram, Twitter, TikTok, Vimeo, Dailymotion,
            Twitch, SoundCloud, Spotify, and more.
          </Typography>
        </Alert>
      </Paper>
    </Container>
  );
};

export default SettingsPage;